import { formatLocalDate } from '@/utils/localDateUtils';
import { useEffect, useState } from 'react';
import { CfePendingItem, CfeTipoLabel } from '@/types/cfe';
import dgiService from '@/services/dgiService';
import DataTable, { Column } from '@/components/common/DataTable';
import MainLayout from '@/components/layout/MainLayout';
import Button from '@/components/common/Button';
import CfeStatusBadge from '@/components/dgi/CfeStatusBadge';
import CfeDetailModal from '@/components/dgi/CfeDetailModal';
import { useDialog } from '@/hooks/useDialog';
import { formatCurrencyTotal } from '@/utils/currencyUtils';
import ResguardoStatusPanel from '@/components/dgi/ResguardoStatusPanel';

export default function CfePendingList() {
  const { showAlert, showConfirm } = useDialog();
  const [rows, setRows] = useState<CfePendingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCfe, setSelectedCfe] = useState<CfePendingItem | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [sendingBatch, setSendingBatch] = useState(false);
  const [view, setView] = useState<'pending' | 'resguardos'>('pending');

  const loadPending = async () => {
    setLoading(true);
    try {
      const res = await dgiService.getPending();
      const data = (res as any).data || res;
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching pending CFEs:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const handleRowClick = (cfe: CfePendingItem) => {
    setSelectedCfe(cfe);
    setShowDetailModal(true);
  };

  const handleCfeUpdated = () => {
    loadPending();
    setShowDetailModal(false);
    setSelectedCfe(null);
    setSelectedIds([]);
  };

  const toggleSelect = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === rows.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(rows.map(r => Number(r.id)));
    }
  };

  const handleBatchSend = async () => {
    if (selectedIds.length === 0) return;
    const confirmed = await showConfirm({
      title: 'Enviar CFE a DGI',
      message: `¿Confirma el envío de ${selectedIds.length} CFE(s) a DGI?`,
      type: 'info',
    });
    if (!confirmed) return;

    setSendingBatch(true);
    try {
      await dgiService.enviarBatch(selectedIds);
      showAlert({
        type: 'success',
        title: 'Envío procesado',
        message: 'Envío procesado. Verifique los resultados.',
      });
      handleCfeUpdated();
    } catch (error) {
      console.error('Error in batch send:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al enviar en lote',
      });
    } finally {
      setSendingBatch(false);
    }
  };

  const columns: Column<CfePendingItem>[] = [
    {
      key: 'select',
      label: '',
      render: (cfe) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(Number(cfe.id))}
          onClick={(e) => toggleSelect(Number(cfe.id), e as any)}
          onChange={() => {}}
          className="h-4 w-4 text-blue-600 rounded border-gray-300"
        />
      ),
    },
    {
      key: 'cfe_tipo_code',
      label: 'Tipo',
      sortable: true,
      render: (cfe) => CfeTipoLabel[cfe.cfe_tipo_code] || `${cfe.cfe_tipo_code}`,
    },
    {
      key: 'cfe_serie',
      label: 'Serie/Nro',
      render: (cfe) => cfe.cfe_serie && cfe.cfe_numero ? `${cfe.cfe_serie}-${cfe.cfe_numero}` : '-',
    },
    {
      key: 'numero_recibo',
      label: 'Recibo',
      sortable: true,
    },
    {
      key: 'numero_contrato',
      label: 'Contrato',
      sortable: true,
    },
    {
      key: 'receptor_nombre',
      label: 'Receptor',
      sortable: true,
      render: (cfe) => cfe.receptor_nombre || '-',
    },
    {
      key: 'monto_total',
      label: 'Monto',
      sortable: true,
      render: (cfe) => formatCurrencyTotal(cfe.monto_total, cfe.moneda),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (cfe) => <CfeStatusBadge cfe={cfe} showType={false} />,
    },
    {
      key: 'created_at',
      label: 'Fecha',
      sortable: true,
      render: (cfe) => formatLocalDate(cfe.created_at, { day: '2-digit', month: '2-digit', year: 'numeric' }),
    },
  ];

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Facturación Electrónica</h1>
            <p className="text-sm text-gray-500">{view === 'pending' ? 'CFEs pendientes de envío a DGI' : 'Resultado de resguardos en Taface y DGI'}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setView(view === 'pending' ? 'resguardos' : 'pending')}>
              {view === 'pending' ? 'Estados de resguardos' : 'Pendientes de envío'}
            </Button>
          </div>
          {view === 'pending' && <div className="flex gap-2">
            {selectedIds.length > 0 && (
              <Button onClick={handleBatchSend} disabled={sendingBatch}>
                {sendingBatch ? 'Enviando...' : `Enviar ${selectedIds.length} seleccionado(s)`}
              </Button>
            )}
            {rows.length > 0 && (
              <Button onClick={toggleSelectAll} variant="secondary">
                {selectedIds.length === rows.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
              </Button>
            )}
          </div>}
        </div>

        {view === 'resguardos' ? <ResguardoStatusPanel /> : <DataTable
          columns={columns}
          data={rows}
          loading={loading}
          searchable
          searchPlaceholder="Buscar por recibo, contrato, receptor..."
          searchFields={['numero_recibo', 'numero_contrato', 'receptor_nombre']}
          onRowClick={handleRowClick}
          rowKey="id"
          emptyState={
            <div className="text-center py-8 text-gray-500">
              No hay CFEs pendientes de envío
            </div>
          }
        />}
      </div>

      <CfeDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        cfe={selectedCfe}
        onCfeUpdated={handleCfeUpdated}
      />
    </MainLayout>
  );
}
